import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const BOOK_IMG = 'https://static.prod-images.emergentagent.com/jobs/01684d0c-4acd-465a-8f1a-34b32ab7bf23/images/26e3376bce1799d8ec591d8c0a7dc9b7ff03257ee9e71c11b88f5ff3a7f80066.png';
const TYPEWRITER_IMG = 'https://static.prod-images.emergentagent.com/jobs/01684d0c-4acd-465a-8f1a-34b32ab7bf23/images/53730a260893d483b61190d74ff152dc43e4bdd1ca883bbc0375f3428800561b.png';

const floatingBooks = [
  { className: 'top-20 left-10 w-32 h-32 opacity-40 animate-float' },
  { className: 'top-40 right-20 w-24 h-24 opacity-30 animate-float-delayed' },
  { className: 'bottom-40 left-1/4 w-28 h-28 opacity-25 animate-float', style: { animationDelay: '2s' } },
  { className: 'bottom-20 right-1/3 w-20 h-20 opacity-35 animate-float-delayed' },
];

const FlyingBooks = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {floatingBooks.map((book) => (
      <img key={book.className} src={BOOK_IMG} alt="" className={`absolute ${book.className}`} style={book.style} />
    ))}
  </div>
);

const HeroText = () => (
  <motion.div
    initial={{ opacity: 0, x: -50 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, ease: 'easeOut' }}
    className="order-2 lg:order-1"
  >
    <p className="text-sm tracking-widest uppercase text-pink-deep font-sans mb-6" data-testid="hero-label">
      Author • Editor • Journalist
    </p>
    <h1
      className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight leading-[1.1] text-brown-deep mb-6"
      data-testid="hero-title"
    >
      Zara Miller
    </h1>
    <div className="w-20 h-1 bg-gradient-to-r from-pink-rose to-pink-deep mb-8"></div>
    <motion.p
      className="text-lg md:text-xl leading-relaxed text-brown-warm mb-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8 }}
      data-testid="hero-description"
    >
      An author, editor, and cultural writer whose work spans fiction, journalism, literary commentary, and editorial strategy.
      Bringing together sharp storytelling, analytical depth, and a distinct voice shaped by publishing, media, and international experience.
    </motion.p>
    <HeroActions />
  </motion.div>
);

const HeroActions = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: 0.8 }}
    className="flex flex-col sm:flex-row items-start gap-4"
  >
    <a
      href="#work"
      className="inline-flex items-center gap-2 rounded-full px-8 py-3 bg-pink-mauve text-white hover:bg-pink-deep transition-all duration-300 font-medium tracking-wide text-sm shadow-soft"
      data-testid="hero-cta-work"
    >
      View My Work
      <ArrowRight size={16} />
    </a>
    <a
      href="#contact"
      className="inline-flex items-center gap-2 rounded-full px-8 py-3 border-2 border-pink-rose text-pink-mauve hover:bg-pink-soft transition-all duration-300 font-medium tracking-wide text-sm"
      data-testid="hero-cta-contact"
    >
      Get In Touch
    </a>
  </motion.div>
);

const HeroPortrait = () => (
  <motion.div
    initial={{ opacity: 0, x: 50 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
    className="order-1 lg:order-2 flex justify-center lg:justify-end"
  >
    <div className="relative">
      <div className="absolute inset-0 -z-10 opacity-20">
        <img src={TYPEWRITER_IMG} alt="" className="w-full h-full object-cover rounded-3xl" />
      </div>
      <div className="absolute -top-6 -left-6 w-full h-full border-2 border-pink-blush rounded-3xl -z-10"></div>
      <div className="absolute -bottom-6 -right-6 w-full h-full bg-gradient-to-br from-pink-soft to-beige-sand rounded-3xl -z-20"></div>
      <img
        src="https://customer-assets.emergentagent.com/job_literary-zara/artifacts/p3zto9cr_headshot.JPG"
        alt="Zara Miller"
        className="w-80 h-96 md:w-96 md:h-[480px] object-cover rounded-3xl shadow-soft-lg"
        data-testid="hero-headshot"
      />
      <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-soft max-w-xs hidden md:block border-2 border-pink-blush">
        <p className="text-sm italic text-brown-warm font-serif mb-2">"Not all those who wander are lost."</p>
        <p className="text-xs text-pink-deep font-sans">— J. R. R. Tolkien</p>
      </div>
    </div>
  </motion.div>
);

const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-beige-light via-pink-soft to-beige-cream">
    <FlyingBooks />
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40 w-full relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <HeroText />
        <HeroPortrait />
      </div>
    </div>
  </section>
);

export default Hero;
